import { Card, Pagination } from "react-bootstrap";
import "./Events.css";
import CustomButton from "../../components/CustomButton/CustomButton";
import CustomTable from "../../components/CustomTable/CustomTable";
import AddEvents from "./AddEvents";
import { useEffect, useRef, useState } from "react"; // ✅ FIX 1: import useRef
import WebService from "../../utility/WebService";
import HelperService from "../../utility/HelperService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import { useNavigate } from "react-router-dom";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import ExportEventsOffcanvas from "./ExportEvents";

// ✅ STEP 1: Import ShareService
import ShareService from "../../utility/ShareService";

// ✅ STEP 2: Define a key for this page's filter state
const PAGE_FILTER_KEY = "events_page_filter";

const Events = () => {
  const navigate = useNavigate();

  // ✅ STEP 3: On first render, check if we have a saved filter state
  // If yes, restore it. If no, use default values.
  const savedFilter = ShareService.getMapValueAndRemove(PAGE_FILTER_KEY);

  const [statusFilter, setStatusFilter] = useState<string>(
    savedFilter?.statusFilter ?? "ALL"
  );
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventIdToDelete, setEventIdToDelete] = useState<string | null>(null);
  const [eventToEdit, setEventToEdit] = useState<any | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(savedFilter?.currentPage ?? 1);
  const [sortField, setSortField] = useState<string>(savedFilter?.sortField ?? "createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(savedFilter?.sortOrder ?? "desc");
  const [uiSortField, setUiSortField] = useState<string>(savedFilter?.uiSortField ?? "createdAtFormatted");
  const itemsPerPage = 10;
  const [search, setSearch] = useState<string>(savedFilter?.search ?? "");

  // ✅ FIX 2: useRef always holds the LATEST search value — no stale closure issue
  const searchRef = useRef<string>(savedFilter?.search ?? "");

  const [showExport, setShowExport] = useState(false);
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  const sortFieldMap: Record<string, string> = {
    createdAtFormatted: "createdAt",
    title: "title",
    startDateTime: "start_date",
    endDateTime: "end_date",
  };

  // ✅ STEP 4: On mount, trigger getEvents with restored state
  // (search is already restored via useState, so just call getEvents normally)
  useEffect(() => {
    getEvents(search);
  }, [currentPage, statusFilter, sortField, sortOrder]);

  const formatDateTime = (value: string) => {
    if (!value) return "-";
    const d = new Date(value);
    return `${HelperService.dateFormatter(d.toISOString())} ${d.toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
      },
    )}`;
  };

  const renderStatus = (status: string) => {
    return (
      <span
        className={`badge ${status === "PUBLISHED" ? "bg-success" : "bg-secondary"}`}
      >
        {status === "PUBLISHED" ? "Published" : "Unpublished"}
      </span>
    );
  };

  const getEvents = (keyword: string = "") => {
    const params = new URLSearchParams();
    // Keyword search
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    if (statusFilter !== "ALL") {
      params.append("status", statusFilter); // PUBLISHED / UNPUBLISHED
    }
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    // Sorting
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);
    WebService.getAPI({
      action: `events/getEvents?${params.toString()}`,
    }).then((res: any) => {
      console.log("Events response:", res?.data?.data);
      const apiData = res?.data?.data || {};
      const perm = res?.data?.permissions || {};
      const PaginationData = res?.data?.pagination || {};

      setPagePermission({
        canView: perm.canView,
        canAdd: perm.canAdd,
        canEdit: perm.canEdit,
        canDelete: perm.canDelete,
      });

      const eventList = apiData || [];

      const formatted = eventList.map((item: any) => ({
        ...item,
        startDateTime: formatDateTime(item.start_date),
        endDateTime: formatDateTime(item.end_date),
        tagName: item.tagName || item.category?.name || "-",
        status: renderStatus(item.status),
        // Show the plain-text Location field, not the map (Google Places) location
        location: item.locationText?.trim() ? item.locationText : "-",

        booking: (
          <>
            {perm.canEdit && (
              <span
                className="cursor-pointer"
                // ✅ STEP 5: Save filter state BEFORE navigating away
                onClick={() => {
                  saveCurrentFilter();
                  navigate(`/event-bookings/${item._id}`);
                }}
                style={{ fontSize: "18px", color: "#007bff" }}
              >
                👁
              </span>
            )}
          </>
        ),

        favourite: (
          <>
            {perm.canEdit && (
              <span
                className="cursor-pointer"
                onClick={() => toggleFavourite(item._id, item.show_favorite)}
                style={{
                  fontSize: "20px",
                  color: item.show_favorite ? "gold" : "#ccc",
                  transition: "0.2s",
                }}
              >
                ★
              </span>
            )}
          </>
        ),

        action: (
          <div>
            {perm.canEdit && (
              <PencilFill
                className="me-3 cursor-pointer"
                onClick={() => {
                  setShowAddEvent(true);
                  setEventToEdit(item);
                }}
              />
            )}
            {perm.canDelete && (
              <TrashFill
                color="#DC143C"
                className="cursor-pointer"
                onClick={() => handleDeleteEvent(item._id)}
              />
            )}
          </div>
        ),
      }));

      setEvents([...formatted]);
      setTotalCount(PaginationData.total || 0);
    });
  };

  // ✅ FIX 3: Use searchRef.current — always the latest value, no stale closure
  const saveCurrentFilter = () => {
    ShareService.setMapValue(PAGE_FILTER_KEY, {
      search: searchRef.current,
      currentPage,
      statusFilter,
      sortField,
      sortOrder,
      uiSortField,
    });
  };

  const toggleFavourite = async (id: any, currentValue: any) => {
    try {
      await WebService.putAPI({
        action: `events/toggleFavourite/${id}`,
        body: { show_favorite: !currentValue },
      });

      const updated = events.map((row) => {
        if (row._id === id) {
          return {
            ...row,
            show_favorite: !currentValue,
            favourite: (
              <span
                className="cursor-pointer"
                onClick={() => toggleFavourite(id, !currentValue)}
                style={{
                  fontSize: "20px",
                  color: !currentValue ? "gold" : "#ccc",
                  transition: "0.2s",
                }}
              >
                ★
              </span>
            ),
          };
        }
        return row;
      });

      setEvents(updated);
      getEvents(search);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteEvent = (eventId: string) => {
    setEventIdToDelete(eventId);
    setShowDeleteModal(true);
  };

  const handleReorder = (newRows: any) => {
    setEvents(newRows);
  };

  return (
    <>
      <AddEvents
        show={showAddEvent}
        handleClose={() => {
          setShowAddEvent(false);
          setEventToEdit(null);
          getEvents(search);
        }}
        data={eventToEdit}
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (eventIdToDelete) {
            WebService.deleteAPI({
              action: `events/deleteEvent/${eventIdToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              getEvents(search);
            });
          }
        }}
      />
      <ExportEventsOffcanvas
        show={showExport}
        onHide={() => setShowExport(false)}
      />
      <div className="events-main">
        <Card className="event-card">
          <div className="mb-3">
            <div className="page-title mb-3">Events</div>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-end gap-3">
                {/* Search Section */}
                <div style={{ width: "300px" }}>
                  <label className="form-label fw-semibold mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Search event..."
                    className="form-control"
                    value={search}
                    onChange={(e) => {
                      const value = e.target.value;
                      searchRef.current = value; // ✅ update ref instantly (sync)
                      setSearch(value);           // update state (async, for UI)
                      setCurrentPage(1);
                      getEvents(value);
                    }}
                  />
                </div>
                {/* Status Filter */}
                <div style={{ width: "200px", marginBottom: "10px" }}>
                  <label className="form-label fw-semibold mb-1">Status</label>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="ALL">All</option>
                    <option value="PUBLISHED">Published</option>
                    <option value="UNPUBLISHED">Unpublished</option>
                  </select>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {pagePermission.canEdit && (
                  <CustomButton
                    label="Export All Bookings"
                    isLoading={false}
                    onClick={() => setShowExport(true)}
                    style={{ width: 180 }}
                  />
                )}
                {pagePermission.canAdd && (
                  <CustomButton
                    isLoading={false}
                    label="Add"
                    onClick={() => {
                      setEventToEdit(null);
                      setShowAddEvent(true);
                    }}
                    style={{ width: 100 }}
                  />
                )}
              </div>
            </div>
          </div>
          <CustomTableSortable
            columns={[
              {
                id: "title",
                label: "Event Name",
                minWidth: 170,
                // sortable: true, // as client said to remove
              },
              {
                id: "startDateTime",
                label: "Start Date & Time",
                minWidth: 100,
                align: "center",
                sortable: true,
              },
              {
                id: "endDateTime",
                label: "End Date & Time",
                minWidth: 100,
                align: "center",
                sortable: true,
              },
              { id: "tagName", label: "Tag", minWidth: 130 },
              { id: "status", label: "Status", minWidth: 120, align: "center" },
              {
                id: "location",
                label: "Location",
                minWidth: 100,
                align: "center",
                // sortable: true,  // as client said to remove
              },
              {
                id: "favourite",
                label: "Favourite",
                minWidth: 80,
                align: "center",
              },
              {
                id: "booking",
                label: "Booking",
                minWidth: 80,
                align: "center",
                // sortable: true,  // as client said to remove
              },

              { id: "action", label: "Action", minWidth: 100, align: "center" },
            ]}
            rows={events}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
            draggable
            onRowsReorder={handleReorder}
          />
        </Card>
      </div>
    </>
  );
};

export default Events;
